// client/src/trajectoryProjectionApprox3D.ts
import { useMemo } from "react";
import { CvJson } from "../../shared/types/cvJson";

export type UvPoint = { u: number; v: number };
export type WorldPoint2D = { x: number; y: number };
export type WorldPoint3D = { x: number; y: number; z: number };

export type ShotTrajectory3D = {
  start?: { location?: WorldPoint3D | null } | null;
  peak?: WorldPoint3D | null;
  end?: { location?: WorldPoint3D | null } | null;
};

export type HomographyParams = {
  h11: number;
  h12: number;
  h13: number;
  h21: number;
  h22: number;
  h23: number;
  h31: number;
  h32: number;
  h33: number; // always 1
};

export type WorldToScreenFn = (x: number, y: number) => UvPoint | null;

function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

function solveLinearSystem(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const a = A.map((row) => row.slice());
  const x = b.slice();

  for (let i = 0; i < n; i++) {
    let pivot = i;
    let maxAbs = Math.abs(a[i][i]);

    for (let r = i + 1; r < n; r++) {
      const val = Math.abs(a[r][i]);
      if (val > maxAbs) {
        maxAbs = val;
        pivot = r;
      }
    }

    if (maxAbs < 1e-10) return null;

    if (pivot !== i) {
      [a[i], a[pivot]] = [a[pivot], a[i]];
      [x[i], x[pivot]] = [x[pivot], x[i]];
    }

    const diag = a[i][i];
    const invDiag = 1 / diag;

    for (let j = i; j < n; j++) {
      a[i][j] *= invDiag;
    }
    x[i] *= invDiag;

    for (let r = 0; r < n; r++) {
      if (r === i) continue;
      const factor = a[r][i];
      if (Math.abs(factor) < 1e-12) continue;

      for (let j = i; j < n; j++) {
        a[r][j] -= factor * a[i][j];
      }
      x[r] -= factor * x[i];
    }
  }

  return x;
}

export function fitWorldToScreenHomographyFromCv(
  cv: CvJson | null,
  maxPairs = 800
): HomographyParams | null {
  if (!cv?.sessions?.length) return null;

  const pairs: { x: number; y: number; u: number; v: number }[] = [];

  try {
    const session = cv.sessions[0];
    const rallies = session.rallies ?? [];

    outer: for (const rally of rallies) {
      const frames = rally.frames ?? [];
      for (const frame of frames) {
        const pcs = frame.player_court_positions ?? [];
        const pts = frame.court?.player_points ?? [];

        for (let i = 0; i < Math.min(pcs.length, pts.length); i++) {
          const pc = pcs[i];
          const pt = pts[i];
          if (!pc || !pt) continue;
          if (typeof pc.x !== "number" || typeof pc.y !== "number") continue;
          if (typeof pt.u !== "number" || typeof pt.v !== "number") continue;
          if (pt.confidence != null && pt.confidence < 0.85) continue;

          pairs.push({ x: pc.x, y: pc.y, u: pt.u, v: pt.v });
          if (pairs.length >= maxPairs) break outer;
        }
      }
    }
  } catch {
    return null;
  }

  if (pairs.length < 6) return null;

  const nParams = 8; // h11,h12,h13,h21,h22,h23,h31,h32
  const AtA: number[][] = Array.from({ length: nParams }, () =>
    Array(nParams).fill(0)
  );
  const Atb: number[] = Array(nParams).fill(0);

  const accumulateRow = (row: number[], rhs: number) => {
    for (let i = 0; i < nParams; i++) {
      const ri = row[i];
      Atb[i] += ri * rhs;
      for (let j = 0; j < nParams; j++) {
        AtA[i][j] += ri * row[j];
      }
    }
  };

  for (const { x, y, u, v } of pairs) {
    const rowU = [x, y, 1, 0, 0, 0, -u * x, -u * y];
    const rowV = [0, 0, 0, x, y, 1, -v * x, -v * y];
    accumulateRow(rowU, u);
    accumulateRow(rowV, v);
  }

  const p = solveLinearSystem(AtA, Atb);
  if (!p) return null;

  const [h11, h12, h13, h21, h22, h23, h31, h32] = p;

  return {
    h11,
    h12,
    h13,
    h21,
    h22,
    h23,
    h31,
    h32,
    h33: 1,
  };
}

export function makeWorldToScreenFromHomography(
  H: HomographyParams | null
): WorldToScreenFn | null {
  if (!H) return null;

  return (x: number, y: number) => {
    const denom = H.h31 * x + H.h32 * y + H.h33;
    if (Math.abs(denom) < 1e-6) return null;

    const u = (H.h11 * x + H.h12 * y + H.h13) / denom;
    const v = (H.h21 * x + H.h22 * y + H.h23) / denom;

    return {
      u: clamp01(u),
      v: clamp01(v),
    };
  };
}

export function homographyJacobianAt(
  H: HomographyParams,
  x: number,
  y: number
) {
  const d = H.h31 * x + H.h32 * y + H.h33;
  if (Math.abs(d) < 1e-9) return null;

  const nu = H.h11 * x + H.h12 * y + H.h13;
  const nv = H.h21 * x + H.h22 * y + H.h23;
  const d2 = d * d;

  const duDx = (H.h11 * d - nu * H.h31) / d2;
  const duDy = (H.h12 * d - nu * H.h32) / d2;
  const dvDx = (H.h21 * d - nv * H.h31) / d2;
  const dvDy = (H.h22 * d - nv * H.h32) / d2;

  return {
    duDx,
    duDy,
    dvDx,
    dvDy,
  };
}

// Average singular value of 2x2 Jacobian.
// No tunable constants; purely local scale of the fitted floor warp.
export function jacobianAverageScaleAt(
  H: HomographyParams,
  x: number,
  y: number
): number | null {
  const J = homographyJacobianAt(H, x, y);
  if (!J) return null;

  const { duDx, duDy, dvDx, dvDy } = J;

  // J^T J
  const a = duDx * duDx + dvDx * dvDx;
  const b = duDx * duDy + dvDx * dvDy;
  const c = duDy * duDy + dvDy * dvDy;

  const trace = a + c;
  const det = a * c - b * b;
  const disc = Math.max(0, trace * trace - 4 * det);
  const sqrtDisc = Math.sqrt(disc);

  const lambda1 = Math.max(0, (trace + sqrtDisc) / 2);
  const lambda2 = Math.max(0, (trace - sqrtDisc) / 2);

  const s1 = Math.sqrt(lambda1);
  const s2 = Math.sqrt(lambda2);

  return (s1 + s2) / 2;
}

export function projectWorldPointApprox3D(
  H: HomographyParams | null,
  worldToScreen: WorldToScreenFn | null,
  point: WorldPoint3D | null | undefined
): UvPoint | null {
  if (!H || !worldToScreen || !point) return null;

  const floor = worldToScreen(point.x, point.y);
  if (!floor) return null;

  const scale = jacobianAverageScaleAt(H, point.x, point.y);
  if (scale == null) return floor;

  return {
    u: floor.u,
    v: clamp01(floor.v - point.z * scale),
  };
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// Quadratic Bezier constrained to pass through start, peak at t=0.5, and end.
export function sampleQuadraticWorldTrajectory(args: {
  start: WorldPoint3D;
  peak: WorldPoint3D;
  end: WorldPoint3D;
  steps?: number;
}): WorldPoint3D[] {
  const { start, peak, end, steps = 20 } = args;
  const control = {
    x: 2 * peak.x - 0.5 * (start.x + end.x),
    y: 2 * peak.y - 0.5 * (start.y + end.y),
    z: 2 * peak.z - 0.5 * (start.z + end.z),
  };

  const pts: WorldPoint3D[] = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;

    const a = {
      x: lerp(start.x, control.x, t),
      y: lerp(start.y, control.y, t),
      z: lerp(start.z, control.z, t),
    };

    const b = {
      x: lerp(control.x, end.x, t),
      y: lerp(control.y, end.y, t),
      z: lerp(control.z, end.z, t),
    };

    pts.push({
      x: lerp(a.x, b.x, t),
      y: lerp(a.y, b.y, t),
      z: lerp(a.z, b.z, t),
    });
  }

  return pts;
}

export function buildTrajectoryPolylineApprox3D(args: {
  H: HomographyParams | null;
  worldToScreen: WorldToScreenFn | null;
  trajectory: ShotTrajectory3D | null | undefined;
  steps?: number;
}): string | null {
  const { H, worldToScreen, trajectory, steps = 20 } = args;

  const start = trajectory?.start?.location;
  const peak = trajectory?.peak;
  const end = trajectory?.end?.location;

  if (
    !H ||
    !worldToScreen ||
    !start ||
    !peak ||
    !end ||
    typeof start.x !== "number" ||
    typeof start.y !== "number" ||
    typeof start.z !== "number" ||
    typeof peak.x !== "number" ||
    typeof peak.y !== "number" ||
    typeof peak.z !== "number" ||
    typeof end.x !== "number" ||
    typeof end.y !== "number" ||
    typeof end.z !== "number"
  ) {
    return null;
  }

  const worldPts = sampleQuadraticWorldTrajectory({
    start,
    peak,
    end,
    steps,
  });

  const uvPts = worldPts
    .map((p) => projectWorldPointApprox3D(H, worldToScreen, p))
    .filter(Boolean) as UvPoint[];

  if (uvPts.length < 2) return null;

  return uvPts.map((p) => `${p.u},${p.v}`).join(" ");
}

export function useApprox3DTrajectoryProjectionFromCv(cv: CvJson | null) {
  return useMemo(() => {
    const H = fitWorldToScreenHomographyFromCv(cv);
    const worldToScreen = makeWorldToScreenFromHomography(H);

    return {
      H,
      worldToScreen,
      projectWorldPointApprox3D: (point: WorldPoint3D | null | undefined) =>
        projectWorldPointApprox3D(H, worldToScreen, point),
      buildTrajectoryPolylineApprox3D: (
        trajectory: ShotTrajectory3D | null | undefined,
        steps = 20
      ) =>
        buildTrajectoryPolylineApprox3D({
          H,
          worldToScreen,
          trajectory,
          steps,
        }),
    };
  }, [cv]);
}
